import React, { useCallback, useMemo, useState } from 'react'
import { Icon, Loading, Tooltip } from 'scala'
import { useLingui } from '@lingui/react'
import { t } from '@lingui/macro'
import styles from './separate-custom.module.scss'
import { SeparateCustomProps } from './types'
import { useSeparationType } from '../../../hooks/tasks'
import { SelectButton } from '../select-button'
import { useAutoSelectUserFreeSeperation } from '../../../hooks/use-instruments-separate-custom'

export const freeSeparationsOptions = [
  {
    title: 'separation.options.4tracks.title',
    description: 'separation.options.4tracks',
    id: 'vocals-drums-bass-other'
  },
  {
    title: 'separation.options.2tracks.title',
    description: 'separation.options.2tracks',
    id: 'vocals-accompaniment'
  }
]

/**
 *
 * Custom Separate: free
 *
 */
export const SeparateCustom: React.FC<SeparateCustomProps> = ({
  openModal,
  methods,
  changeOperations,
  isChangeOperations,
  newChangeSeparation,
  processing,
  processingStem
}) => {
  const { i18n } = useLingui()
  const { getTitle } = useSeparationType()
  const [showBackDrop, setShowBackDrop] = useState(false)

  const {
    selectedInstruments,
    getFilterInstruments,
    getFilterMultimedia,
    loading,
    toggleInstrument: onClick,
    clearInstrumentsSelected
  } = methods

  const instruments = getFilterInstruments()
  const multimedia = getFilterMultimedia()

  const isFreeSeparation = useMemo(() => {
    return changeOperations.currentSeparationType !== 'custom'
  }, [changeOperations])

  useAutoSelectUserFreeSeperation({
    isFree: true,
    defaultOption: changeOperations.selectedSeparation?.params?.type,
    toggleInstrument: onClick,
    clearInstrumentsSelected,
    isSeparateB: changeOperations.currentSeparationType !== 'custom'
  })

  const onSelected = useCallback(
    (instrument: string, category: string, idGroup?: string) => {
      onClick(instrument, category, idGroup, !!newChangeSeparation)
    },
    [newChangeSeparation, onClick]
  )
  if (loading) {
    return (
      <div className={styles.containerLoading}>
        <Loading />
      </div>
    )
  }

  return (
    <div className="flex relative flex-col gap-5">
      {showBackDrop && <div className={styles.backdrop} />}

      <div>
        <div className={styles.header2}>
          <h3 className="text-[#d1d1d1] text-sm font-medium">
            {i18n._(t`basic_track_separation_title`)}
          </h3>
        </div>
        <div className={styles.buttonsContainer}>
          {freeSeparationsOptions.map((separation) => {
            const isSelected = selectedInstruments?.[0] === separation.id

            return (
              <SelectButton
                key={separation.id}
                id={separation.id}
                label={i18n._(separation.title)}
                subtitle={i18n._(separation.description)}
                onClick={onSelected}
                isSelected={isSelected}
                category="freeSeparation"
                showCloseButton
                processingStem={processingStem}
                isProcessing={processing}
              />
            )
          })}
        </div>
      </div>

      {instruments && (
        <div>
          <div className={styles.header}>
            <h3 className="text-[#d1d1d1] text-sm font-medium">
              {i18n._(t`custom_track_separation_title`)}
            </h3>

            <div>
              <span className={styles.iconTooltip}>
                <Icon
                  name="info-outlined"
                  width={16}
                  height={16}
                  className="text-[#d1d1d1]"
                />
              </span>
              <Tooltip
                className={styles.tooltip}
                title={i18n._(t`separation_tracks_tip`)}
                top
              />
            </div>
          </div>
          <div className={styles.buttonsContainer}>
            {instruments.map((stem) => {
              const isSelected = stem?.stems?.find((item: { id: string }) =>
                selectedInstruments?.includes(item?.id)
              )

              const isSelectedLabel = isSelected?.label

              const isSelectedId = isSelected?.id

              const label = i18n._(isSelectedLabel || stem.label)
              const haveDropdown =
                stem?.stems?.length > 1 ? stem.stems : undefined

              return (
                <SelectButton
                  key={stem.id}
                  id={stem.id}
                  label={label}
                  icon={stem.icon?.svg}
                  dropdown={haveDropdown}
                  isSelectedDropdown={isFreeSeparation ? '' : isSelectedId}
                  paywall={stem.stems?.[0]?.paywall}
                  onClick={onSelected}
                  openModal={openModal}
                  isSelected={
                    isFreeSeparation
                      ? false
                      : selectedInstruments.includes(stem.id)
                  }
                  category="musical"
                  setShowBackDrop={setShowBackDrop}
                  isChangeOperations={isChangeOperations}
                />
              )
            })}
          </div>
        </div>
      )}

      {multimedia && (
        <div>
          <div className={styles.header2}>
            <p className="text-[#d1d1d1] text-sm font-medium">
              {i18n._(t`separation_multimedia_title`)}
            </p>
            <p className="text-[#727272] text-sm font-medium mt-1">
              {i18n._(t`separation_multimedia_description`)}
            </p>
          </div>
          <div className="flex gap-4 flex-wrap mt-2">
            {multimedia.map((stem) => (
              <SelectButton
                key={stem.id}
                id={stem.id}
                label={i18n._(stem.label)}
                subtitle={getTitle(stem.stems?.[0]?.stems?.length)}
                paywall={stem.stems?.[0]?.paywall}
                onClick={onClick}
                openModal={openModal}
                isSelected={false}
                category="multimedia"
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
